import {useMemo} from 'react';

import {FieldArray, Form, Formik} from 'formik';
import {useTranslation} from 'react-i18next';

// Material UI

import {AddCircleOutlineRounded, DeleteOutline} from '@mui/icons-material';
import {
  Box,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useTheme,
} from '@mui/material';

import {CompanyProfileDialogButtons} from '../CompanyProfileDialogButtons';

import {getFieldStyle} from '../../../../../layouts/theme/FieldStyles';
import {CompanyProfile, Benefit} from '../../types';

type CompanyProfileBenefitsFormProps = {
  company: CompanyProfile;
  onCancel(): void;
  onSubmit(value: CompanyProfile['benefits']): void;
};

// Function to create a benefit record with some sane defaults
const makeBenefit = (opts?: Partial<Benefit>): Omit<Benefit, 'resourceId'> => ({
  benefitKey: opts?.benefitKey ?? '',
  category: {
    categoryKey: opts?.category?.categoryKey ?? '',
    labels: {
      de: opts?.category?.labels.de ?? '',
      en: opts?.category?.labels.en ?? '',
    },
  },
  labels: {
    de: opts?.labels?.de ?? '',
    en: opts?.labels?.en ?? '',
  },
  selectedAt: new Date().toISOString(),
});

export const CompanyProfileAddBenefitsForm = ({company, onCancel, onSubmit}: CompanyProfileBenefitsFormProps) => {
  const {t} = useTranslation();
  const theme = useTheme();
  const fieldStyle = getFieldStyle(theme);

  // This function creates collection of benefits per category
  const benefitsByCategory = useMemo(() => {
    const categorized: Record<string, Array<Benefit>> = {};

    company.benefits?.map((benefit) => {
      if (categorized[benefit.category.categoryKey]) {
        categorized[benefit.category.categoryKey].push(benefit);
      } else {
        categorized[benefit.category.categoryKey] = [benefit];
      }
    });

    return categorized;
  }, [company.benefits]);

  // Empty benefit list with a placeholder
  const benefitsList = [makeBenefit()];

  const onFormikSubmit = async (value: any) => {
    // Fill the category labels based on the selection onSubmit
    for (const benefit of value.benefits as Benefit[]) {
      benefit.benefitKey = String(benefit.labels.en).toLowerCase();
      benefit.category.labels = benefitsByCategory[benefit.category.categoryKey][0].category.labels;
    }

    onSubmit(value.benefits as Benefit[]);
  };

  return (
    <Formik initialValues={{benefits: benefitsList}} onSubmit={onFormikSubmit}>
      {(formik) => (
        <Form>
          <FieldArray name='benefits'>
            {(listHelpers) => (
              <Box sx={{pt: 1}}>
                {formik.values.benefits.map((benefit, index) => (
                  <Grid container spacing={2} mb={2} key={index} alignItems='flex-start'>
                    <Grid item sm={3} xs={12}>
                      <FormControl fullWidth sx={{...fieldStyle}}>
                        <InputLabel id='demo-simple-select-label'>
                          {t('companyProfile.benefits.categoryLabel')}
                        </InputLabel>
                        <Select
                          name={`benefits.${index}.category.categoryKey`}
                          label={t('companyProfile.benefits.categoryLabel')}
                          value={benefit.category.categoryKey}
                          onChange={listHelpers.form.handleChange}
                          sx={{textTransform: 'capitalize'}}
                          fullWidth
                          required
                        >
                          {/* Category names as menu items */}
                          {Object.keys(benefitsByCategory).map((categoryKey, key) => (
                            <MenuItem key={key} value={categoryKey} sx={{textTransform: 'capitalize'}}>
                              {categoryKey}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={7}>
                      {/* TODO: This sould be replaced with the EmpionTextfield, 
                      when we are able to use the InputProps */}
                      <TextField
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <span className={'fi fi-de'} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{...fieldStyle}}
                        required
                        label='Leistung'
                        name={`benefits.${index}.labels.de`}
                        id='benefit_name'
                        fullWidth
                        value={benefit.labels.de}
                        onChange={listHelpers.form.handleChange}
                        inputProps={{maxLength: 50}}
                      />
                      {/* TODO: This sould be replaced with the EmpionTextfield,
                       when we are able to use the InputProps */}
                      <TextField
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <span className={'fi fi-gb'} />
                            </InputAdornment>
                          ),
                        }}
                        label='Benefit'
                        name={`benefits.${index}.labels.en`}
                        id='benefit_name'
                        fullWidth
                        value={benefit.labels.en}
                        onChange={listHelpers.form.handleChange}
                        inputProps={{maxLength: 50}}
                        sx={{
                          marginTop: '8px',
                          marginBottom: '16px',
                          ...fieldStyle,
                        }}
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm='auto' mt={{sm: 1}}>
                      {/* only shows up in the last element */}
                      {formik.values.benefits.length - 1 == index && (
                        <IconButton onClick={() => listHelpers.push(makeBenefit())}>
                          <AddCircleOutlineRounded />
                        </IconButton>
                      )}

                      {/* Only enables removing if it's not the only element (otherwise the form will be empty) */}
                      {formik.values.benefits.length > 1 && (
                        <IconButton onClick={() => listHelpers.remove(index)}>
                          <DeleteOutline />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Box>
            )}
          </FieldArray>

          {/* Buttons */}
          <Grid item container justifyContent={'flex-end'} display={'flex'}>
            <CompanyProfileDialogButtons onCancel={onCancel} />
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
